import { useEffect, useRef, useState } from "react";
import { LogOut, Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/common";
import { UserAvatar } from "@/components/UserAvatar";
import { LanguageSelect } from "@/components/LanguageSelect";
import { AdjustmentHistory } from "@/components/Adjustments";
import { DeleteAccountPanel } from "@/components/DeleteAccount";
import { EmailChangePanel } from "@/components/EmailChange";
import { ROLE_LABEL } from "@/lib/perm";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function Profilo() {
  const { user, setUser, logout } = useAuth();
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", theme: user.theme || "system", reference_partner_id: user.reference_partner_id || "none" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [partners, setPartners] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [adjustments, setAdjustments] = useState([]);
  useEffect(() => { api.get("/me/adjustments").then((r) => setAdjustments(r.data)); }, []);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data.filter((p) => p.id !== user.id))); }, [user.id]);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Immagine troppo grande (max 5 MB)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/avatar", fd);
      setUser(data);
      toast.success("Foto profilo aggiornata");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const removeAvatar = async () => { const { data } = await api.delete("/me/avatar"); setUser(data); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/me", { ...f, reference_partner_id: f.reference_partner_id === "none" ? null : f.reference_partner_id });
      setUser(data);
      toast.success("Profilo aggiornato");
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password aggiornata");
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  return (
    <div className="max-w-3xl" data-testid="profile-page">
      <PageHeader title="Profilo" actions={<Button variant="outline" onClick={logout} data-testid="profile-logout-button"><LogOut className="mr-1.5 h-4 w-4" />Esci</Button>} />
      <div className="mb-6 flex items-center gap-4 rounded-lg border bg-card p-5 fade-up">
        <UserAvatar fileId={user.avatar_file_id} name={user.name} className="font-display h-16 w-16 text-lg" testId="profile-avatar" />
        <div className="flex-1">
          <p className="text-lg font-semibold" data-testid="profile-name">{user.name}</p>
          <p className="text-sm text-muted-foreground">{user.email} · {ROLE_LABEL[user.role] || "Dipendente"}{user.is_partner && " · Socio"}</p>
          <p className="text-xs text-muted-foreground">{[user.job_title, user.department, user.location].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-file-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="profile-avatar-upload-button"><Camera className="mr-1.5 h-4 w-4" />{user.avatar_file_id ? "Cambia foto" : "Carica foto"}</Button>
          {user.avatar_file_id && <Button size="sm" variant="ghost" onClick={removeAvatar} data-testid="profile-avatar-remove-button">Rimuovi</Button>}
        </div>
      </div>
      <div className="space-y-6">
        <Panel title="Dati personali e preferenze">
          <form onSubmit={save} className="grid gap-4 p-5 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nome e cognome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="profile-name-input" /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone-input" /></div>
            <div className="space-y-1.5">
              <Label>Tema</Label>
              <Select value={f.theme} onValueChange={(v) => setF({ ...f, theme: v })}>
                <SelectTrigger data-testid="profile-theme-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="system">Predefinito aziendale</SelectItem><SelectItem value="light">Giorno</SelectItem><SelectItem value="dark">Notte</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Referente (socio o amministratore)</Label>
              <Select value={f.reference_partner_id} onValueChange={(v) => setF({ ...f, reference_partner_id: v })}>
                <SelectTrigger data-testid="profile-partner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.role === "admin" ? " · Amministratore" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Button type="submit" data-testid="profile-save-button">Salva</Button></div>
          </form>
        </Panel>
        <Panel title="Lingua">
          <div className="p-5"><LanguageSelect testId="profile-language-select" onChange={(v) => api.put("/me", { language: v }).catch(() => {})} /></div>
        </Panel>
        <Panel title="Rettifiche del mio saldo">
          <div className="p-5"><AdjustmentHistory items={adjustments} testId="profile-adjustments" /></div>
        </Panel>
        <Panel title="Cambia password">
          <form onSubmit={changePw} className="grid gap-4 p-5 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Password attuale</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="profile-current-password-input" /></div>
            <div className="space-y-1.5"><Label>Nuova password (min. 8)</Label><Input type="password" minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required data-testid="profile-new-password-input" /></div>
            <div className="sm:col-span-2"><Button type="submit" variant="outline" data-testid="profile-password-button">Aggiorna password</Button></div>
          </form>
        </Panel>
        <EmailChangePanel />
        <DeleteAccountPanel />
      </div>
    </div>
  );
}
